import {
  CITY_STATE_COUNTRY_ADDRESS_PREFERENCE,
  DO_NOT_SHOW_ADDRESS_PREFERENCE,
  FULL_ADDRESS_ADDRESS_PREFERENCE,
  STATE_COUNTRY_ADDRESS_PREFERENCE,
} from '@/constants/addresses'
import { AddressFormat } from '@paypalcorp/worldready'
import { PortableAddress } from '../../types/world-ready'
import { useWorldReadyContext } from './'

/**
 * Get new WorldReady AddressFormat constructor
 * @param region region of address to format, defaults to worldReady.region
 * @param style formatting style, defaults to 'singleline'
 * @param type address type
 * @param titleCase title-casing options; only valid with 'international-*' styles.
 *   Note the key is `addressRegion` (not `region` as the public guide shows).
 */
export function useAddressFormatter({
  type,
  region,
  style,
  titleCase,
}: {
  type?: string
  region?: string
  style?: string
  titleCase?: { addressRegion?: boolean }
} = {}) {
  const worldReady = useWorldReadyContext()
  const addressRegion = region || worldReady.region

  return new AddressFormat(worldReady, {
    addressRegion,
    type,
    style: style || 'singleline',
    titleCase: titleCase ?? {},
  })
}

/**
 * Format an address to a string using WorldReady AddressFormat
 * @param region region of address to format, defaults to worldReady.region
 * @param style formatting style, defaults to 'singleline'
 * @param type address type
 */
export function useAddressFormatting(
  addressToFormat: Partial<PortableAddress>,
  type?: string,
  region?: string,
  style?: string,
) {
  const worldReady = useWorldReadyContext()
  const addressRegion = region || worldReady.region

  try {
    return new AddressFormat(worldReady, {
      addressRegion,
      type,
      style: style || 'singleline',
    }).format({ ...addressToFormat })
  } catch (_e) {
    return ''
  }
}

export function useFormattedAddresses(address: PortableAddress) {
  const { adminArea1, adminArea2, countryCode } = address || {}
  const addressFormatter = useAddressFormatter({
    region: countryCode,
    style: 'international-singleline',
    titleCase: { addressRegion: true },
  })

  let fullAddressOption = ''
  let cityStateCountryOption = ''
  let stateCountryOption = ''

  try {
    const partialAddresses = addressFormatter.getPartialAddresses({
      ...address,
    })

    fullAddressOption = partialAddresses[0] || ''

    // getPartialAddresses returns descending-detail tiers whose contents depend
    // on which admin areas are present; map them to our display preferences.
    if (adminArea2 && adminArea1) {
      cityStateCountryOption = partialAddresses[1] || ''
      stateCountryOption = partialAddresses[2] || ''
    } else if (adminArea2) {
      cityStateCountryOption = partialAddresses[1] || ''
    } else if (adminArea1) {
      stateCountryOption = partialAddresses[1] || ''
    }
  } catch (_e) {
    // Address metadata not loaded or address invalid — fall back to empty options.
  }

  return {
    [DO_NOT_SHOW_ADDRESS_PREFERENCE as string]: '',
    [FULL_ADDRESS_ADDRESS_PREFERENCE]: fullAddressOption,
    [CITY_STATE_COUNTRY_ADDRESS_PREFERENCE]: cityStateCountryOption,
    [STATE_COUNTRY_ADDRESS_PREFERENCE]: stateCountryOption,
  }
}
